import React, { useState } from 'react';
import { Plus, ChevronDown, Download, ArrowRightLeft } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';
import { updateSectionLocation } from '../lib/updateSectionLocation';

export default function GoldFileSection({
  open,
  toggle
}: {
  open: boolean;
  toggle: (key: string) => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [moving, setMoving] = useState(false);

  const handleDownloadGoldenFile = async () => {
    try {
      setIsDownloading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const response = await fetch('https://cdnbjnapvlbmarhuflsv.supabase.co/functions/v1/generate_combined_gold_file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch Golden File');

      const data = await response.json();
      const fullName = session.user.user_metadata?.name || 'User';

      const blob = new Blob([data.textContent || ''], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Gold File - ${fullName}.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Golden file download error:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleMove = async () => {
    setMoving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not logged in');

      const { data } = await supabase
        .from('customizations')
        .select('section_locations')
        .eq('user_id', userId)
        .single();

      const current = data?.section_locations?.gold_file === 'tools' ? 'tools' : 'loop';
      const next = current === 'tools' ? 'loop' : 'tools';

      await updateSectionLocation('gold_file', next);
      window.location.reload();
    } catch (error) {
      console.error('Failed to move section:', error);
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => toggle('gold_file')}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <Plus className="w-6 h-6 text-[#236caf]" />
          Gold File
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            open ? 'rotate-180' : ''
          )}
        />
      </button>

      {open && (
        <div className="p-4 sm:p-6 space-y-4">
          <Button
            variant="outline"
            onClick={handleDownloadGoldenFile}
            disabled={isDownloading}
            className="w-full flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            {isDownloading ? 'Downloading...' : 'Download Golden File'}
          </Button>

          <Button
            variant="ghost"
            onClick={handleMove}
            disabled={moving}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-600"
          >
            <ArrowRightLeft className="w-4 h-4" />
            {moving ? 'Moving...' : 'Move to Other Page'}
          </Button>
        </div>
      )}
    </div>
  );
}
